import React from 'react';
import { Link } from '@tanstack/react-router';
import { Card, CardContent } from '@/components/ui/card';
import { CardBlock } from '@/components/card/card-block';
const contactEmail = 'support@adora.media';
export const PrivacyContent = () => {
    return (<section className="mx-auto max-w-5xl px-6 md:px-8 py-10 md:py-14 space-y-6">
            <Intro />

            <CardBlock title="Information we collect">
                <ul className="list-disc pl-5 space-y-2 text-sm md:text-base">
                    <li>
                        <strong>Account data:</strong> name, email, hashed passwords, preferred language.
                    </li>
                    <li>
                        <strong>Workspace / organization data:</strong> account name, basic profile details you provide
                        about your brand, team, or clients.
                    </li>
                    <li>
                        <strong>Campaign & tracking data:</strong> campaign names and descriptions, images you upload,
                        tracking URLs, QR code identifiers, wallet/credit usage (e.g., when a campaign consumes a
                        credit).
                    </li>
                    <li>
                        <strong>Analytics events:</strong> anonymised scan/visit information (timestamp, campaign ID,
                        tracking link ID, hashed IP, approximate region, user agent, referer when available).
                    </li>
                    <li>
                        <strong>Usage data:</strong> app interactions such as pages viewed, buttons clicked, and general
                        feature usage in aggregate, to help us understand how Adora is used.
                    </li>
                    <li>
                        <strong>Support:</strong> messages or attachments you send to support (email, in-app, or other
                        channels we provide).
                    </li>
                </ul>
            </CardBlock>

            <CardBlock title="How we use your information">
                <ul className="list-disc pl-5 space-y-2 text-sm md:text-base">
                    <li>Provide and maintain Adora features (campaign creation, tracking links, QR analytics).</li>
                    <li>Compute & display metrics such as scan counts, basic trends, and per-campaign performance.</li>
                    <li>Secure the service (fraud prevention, abuse detection, rate limiting, anomaly detection).</li>
                    <li>
                        Improve the product (aggregated analytics, feature diagnostics, user experience improvements).
                    </li>
                    <li>Communicate about changes, outages, onboarding tips, or support.</li>
                    <li>Process payments and token/credit status for paid packs (when enabled).</li>
                </ul>
            </CardBlock>

            <CardBlock title="Legal bases (where applicable)">
                <ul className="list-disc pl-5 space-y-2 text-sm md:text-base">
                    <li>
                        <strong>Contract:</strong> to deliver the Adora service you sign up for (e.g., managing
                        campaigns and analytics).
                    </li>
                    <li>
                        <strong>Legitimate interests:</strong> to keep our platform secure, prevent abuse, and improve
                        features.
                    </li>
                    <li>
                        <strong>Consent:</strong> where required (for example, certain marketing emails or optional
                        analytics).
                    </li>
                    <li>
                        <strong>Legal obligations:</strong> to comply with applicable laws, tax, and accounting rules.
                    </li>
                </ul>
            </CardBlock>

            <CardBlock title="Data retention">
                <p className="text-sm md:text-base">
                    We keep account and campaign data for as long as your account is active. You can request deletion
                    (see “Your rights”). Backups and logs are retained for a limited period for security and continuity.
                    If you cancel, we may keep a read-only copy for a reasonable time so you can retrieve records or so
                    we can meet legal obligations.
                </p>
            </CardBlock>

            <CardBlock title="Sharing & processors">
                <p className="text-sm md:text-base mb-3">
                    We don’t sell your data. We may share limited data with trusted service providers who help us
                    operate Adora:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-sm md:text-base">
                    <li>Cloud hosting & CDN (infrastructure, storage, backups).</li>
                    <li>Email & messaging (transactional emails, onboarding, support).</li>
                    <li>Analytics (usage patterns in aggregate, error monitoring).</li>
                    <li>Payment processing (for campaign token packs or subscriptions, when used).</li>
                </ul>
                <p className="text-sm md:text-base mt-3">
                    These providers process data on our behalf and under agreements that require confidentiality and
                    security.
                </p>
            </CardBlock>

            <CardBlock title="International transfers">
                <p className="text-sm md:text-base">
                    Your data may be stored or processed in data centers outside your country. We use safeguards such as
                    contractual commitments with our processors to help protect your information across borders, where
                    required by law.
                </p>
            </CardBlock>

            <CardBlock title="Security">
                <ul className="list-disc pl-5 space-y-2 text-sm md:text-base">
                    <li>Encryption in transit (HTTPS) and at rest where applicable.</li>
                    <li>Role-based access for internal operations; least-privilege by default.</li>
                    <li>Backups and monitoring to detect anomalies and recover from incidents.</li>
                    <li>
                        Hashing or pseudonymising certain technical identifiers (such as IP addresses used for
                        analytics) to reduce identifiability.
                    </li>
                </ul>
                <p className="text-xs text-muted-foreground mt-3">
                    No method of transmission or storage is 100% secure. We work to continually improve safeguards.
                </p>
            </CardBlock>

            <CardBlock title="Cookies & analytics">
                <p className="text-sm md:text-base">
                    We use essential cookies for authentication, security, and basic functionality (for example, keeping
                    you signed in). We may use analytics cookies or similar technologies to understand usage and improve
                    features, often in aggregate form. You can control non-essential cookies via your browser settings
                    or in-app controls (if available).
                </p>
            </CardBlock>

            <CardBlock title="Payments & credits">
                <p className="text-sm md:text-base">
                    When you purchase campaign packs or credits, payments are processed by a third-party provider. We
                    don’t store full payment card details on our servers. The payment processor acts as a separate
                    controller or processor under its own privacy policy. We may store limited payment metadata
                    (transaction IDs, amounts, currency, timestamps) to manage your credits and billing history.
                </p>
            </CardBlock>

            <CardBlock title="Your rights">
                <ul className="list-disc pl-5 space-y-2 text-sm md:text-base">
                    <li>Access a copy of your personal data we hold, where applicable.</li>
                    <li>Correct or update inaccurate information.</li>
                    <li>Delete data (subject to legal, security, or contractual limits).</li>
                    <li>Export certain data where technically feasible.</li>
                    <li>Object to or restrict certain processing where applicable under local law.</li>
                </ul>
                <p className="text-sm md:text-base mt-3">
                    To exercise rights, contact us at{' '}
                    <a className="underline underline-offset-2" href={`mailto:${contactEmail}`}>
                        {contactEmail}
                    </a>
                    . We may need to verify your identity before responding.
                </p>
            </CardBlock>

            <CardBlock title="Team access & roles">
                <p className="text-sm md:text-base">
                    If your account supports multiple users, you or your organization admins can invite teammates and
                    control who can view or edit campaigns, tracking links, and analytics. Access to data within your
                    Adora account is governed by your internal roles and choices. In a single-owner setup, only the
                    account owner manages data.
                </p>
            </CardBlock>

            <CardBlock title="Children">
                <p className="text-sm md:text-base">
                    Adora is not intended for children under 16. We don’t knowingly collect personal data from children.
                    If you believe a child has provided us personal information, contact us so we can investigate and,
                    where appropriate, delete the data.
                </p>
            </CardBlock>

            <CardBlock title="Changes to this policy">
                <p className="text-sm md:text-base">
                    We may update this policy to reflect changes in our practices, features, or legal requirements.
                    We’ll post the updated date at the top of the page and, if changes are material, we may notify you
                    through the app or by email.
                </p>
            </CardBlock>

            <CardBlock title="Contact">
                <p className="text-sm md:text-base">
                    Questions about privacy? Email{' '}
                    <a className="underline underline-offset-2" href={`mailto:${contactEmail}`}>
                        {contactEmail}
                    </a>
                    .
                </p>
                <div className="mt-4 text-sm">
                    <Link to="/terms" className="underline underline-offset-2">
                        Terms of Service
                    </Link>
                </div>
            </CardBlock>
        </section>);
};
function Intro() {
    return (<Card>
            <CardContent className="p-6 md:p-8 space-y-3">
                <p className="text-sm md:text-base">
                    Adora (“we”, “us”, “our”) provides a platform for managing campaigns, tracking links, QR codes, and
                    related analytics for offline and street-level advertising. This Privacy Policy explains what we
                    collect, how we use it, and your choices. By using Adora, you agree to this policy.
                </p>
                <p className="text-xs text-muted-foreground">
                    This page is for transparency and does not limit any rights you may have under local law.
                </p>
            </CardContent>
        </Card>);
}
PrivacyContent.displayName = 'PrivacyContent';
